import { isNativePlatform } from "@/lib/native";
import { isIosStandalonePwa, isStandalonePwa } from "@/lib/pwaInstall";

/** Matches Tailwind `md` — bottom nav and mobile shell treatments. */
export const MOBILE_SHELL_MQ = "(max-width: 767px)";

/** Sheet-based rail / top-bar workspace controls (tablet + phone). */
export const MOBILE_SHEET_MQ = "(max-width: 1023px)";

/**
 * Home-indicator gaps are typically 21–34px. Anything larger is the
 * on-screen keyboard shrinking the visual viewport.
 */
export const KEYBOARD_OPEN_THRESHOLD_PX = 80;

/**
 * Mobile Safari / Chrome URL bar + toolbar can shrink the visual viewport by
 * ~90–160px. Only treat a larger gap as the on-screen keyboard so the bottom
 * nav stays visible while browser chrome is showing.
 */
export const BROWSER_CHROME_KEYBOARD_THRESHOLD_PX = 200;

export function isMobileShellViewport() {
  return window.matchMedia(MOBILE_SHELL_MQ).matches;
}

export function isMobileSheetViewport() {
  return window.matchMedia(MOBILE_SHEET_MQ).matches;
}

/** Layout viewport width — stable under browser zoom (unlike 100vw). */
export function layoutViewportWidth() {
  return window.innerWidth;
}

/** Layout viewport height. */
export function layoutViewportHeight() {
  return window.innerHeight;
}

export interface ViewportBox {
  layoutHeight: number;
  visualHeight: number;
  visualOffsetTop: number;
  visualWidth?: number;
  layoutWidth?: number;
  /** iOS home-screen PWA — needs an explicit shell height (see resolve). */
  iosStandalone?: boolean;
  /** Bottom safe-area inset (px) added to the pinned height on iOS standalone. */
  safeBottom?: number;
  /** Capacitor Keyboard plugin height — reliable when the WebView does not resize. */
  nativeKeyboardHeight?: number;
  /** Mobile browser tab (not installed PWA / native) — track URL bar / toolbar. */
  trackBrowserChrome?: boolean;
}

/** Pinch-zoom shrinks visualViewport.width vs the layout viewport. */
export const VIEWPORT_ZOOM_THRESHOLD_PX = 8;

export interface ResolvedMobileShellViewport {
  /**
   * Pixel height to pin `--app-vvh` to, or `null` when CSS should own the
   * height (`100vh` in the PWA, `100dvh` in a mobile browser tab).
   */
  height: number | null;
  offsetTop: number;
  keyboardOpen: boolean;
}

export function isPinchZoomed(box: ViewportBox): boolean {
  if (box.visualWidth == null || box.layoutWidth == null) return false;
  return Math.abs(box.visualWidth - box.layoutWidth) > VIEWPORT_ZOOM_THRESHOLD_PX;
}

/** In-browser mobile Safari / Chrome — URL bar and toolbar overlay the page. */
export function shouldTrackBrowserChrome(): boolean {
  return isMobileShellViewport() && !isStandalonePwa() && !isNativePlatform();
}

/**
 * Mobile shell sizing.
 *
 * Keyboard open: pin the shell to the visual viewport so chrome stays above the
 * keyboard.
 *
 * Keyboard closed, iOS home-screen PWA: Safari clamps `position: fixed` / `100vh`
 * to the layout viewport, which stops above the home indicator and leaves a dead
 * strip. Return an explicit height of `layoutHeight + safeBottom` so the shell
 * covers the home-indicator band; chrome padding keeps tap targets above it.
 *
 * Keyboard closed, mobile browser tab: pin to the visual viewport so the bottom
 * nav rides above the URL bar / toolbar as they show and hide (`100dvh` is the
 * CSS fallback). Skip the pin while pinch-zoomed so we do not fight zoom.
 *
 * Keyboard closed, installed PWA / native: return `height: null` so CSS
 * (`100vh`) owns the height.
 */
export function resolveMobileShellViewport(
  box: ViewportBox,
): ResolvedMobileShellViewport {
  const nativeKeyboardHeight = box.nativeKeyboardHeight ?? 0;
  if (nativeKeyboardHeight > 0) {
    return {
      height: Math.round(Math.max(0, box.layoutHeight - nativeKeyboardHeight)),
      offsetTop: 0,
      keyboardOpen: true,
    };
  }

  if (box.trackBrowserChrome && isPinchZoomed(box)) {
    return {
      height: null,
      offsetTop: 0,
      keyboardOpen: false,
    };
  }

  const vvSpan = box.visualHeight + box.visualOffsetTop;
  const coveredBelow = Math.max(0, box.layoutHeight - vvSpan);
  const pannedForKeyboard = box.visualOffsetTop > KEYBOARD_OPEN_THRESHOLD_PX;
  const shrinkThreshold = box.trackBrowserChrome
    ? BROWSER_CHROME_KEYBOARD_THRESHOLD_PX
    : KEYBOARD_OPEN_THRESHOLD_PX;
  const keyboardOpen =
    coveredBelow > shrinkThreshold || pannedForKeyboard;

  if (keyboardOpen) {
    return {
      height: Math.round(box.visualHeight),
      offsetTop: Math.round(box.visualOffsetTop),
      keyboardOpen: true,
    };
  }

  if (box.iosStandalone) {
    return {
      height: Math.round(box.layoutHeight + (box.safeBottom ?? 0)),
      offsetTop: 0,
      keyboardOpen: false,
    };
  }

  if (box.trackBrowserChrome && !isPinchZoomed(box)) {
    return {
      height: Math.round(box.visualHeight),
      offsetTop: Math.round(box.visualOffsetTop),
      keyboardOpen: false,
    };
  }

  return {
    height: null,
    offsetTop: 0,
    keyboardOpen: false,
  };
}

function requestOverlayVirtualKeyboard() {
  const vk = (
    navigator as Navigator & {
      virtualKeyboard?: { overlaysContent: boolean };
    }
  ).virtualKeyboard;
  if (vk) vk.overlaysContent = true;
}

/** Bottom safe-area inset seeded onto `--sab` by native.ts / the boot script. */
function readSeededSafeBottom(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--sab");
  return parseFloat(raw) || 0;
}

/** Set by Capacitor Keyboard listeners while the native keyboard is visible. */
let nativeKeyboardHeight = 0;

async function bindNativeKeyboard(onChange: () => void): Promise<() => void> {
  if (!isNativePlatform()) return () => {};

  try {
    const { Keyboard } = await import("@capacitor/keyboard");
    const show = await Keyboard.addListener("keyboardWillShow", (info) => {
      nativeKeyboardHeight = info.keyboardHeight;
      onChange();
    });
    const hide = await Keyboard.addListener("keyboardWillHide", () => {
      nativeKeyboardHeight = 0;
      onChange();
    });
    return () => {
      show.remove();
      hide.remove();
      nativeKeyboardHeight = 0;
    };
  } catch {
    return () => {
      nativeKeyboardHeight = 0;
    };
  }
}

/**
 * Sync CSS viewport vars used by the shell.
 * Mobile browser: visual viewport so the nav tracks URL bar / toolbar chrome.
 * PWA / native: layout viewport (`100vh`) when the keyboard is closed, visual
 * viewport when it is open.
 */
export function syncViewportMetrics() {
  const root = document.documentElement;
  const layoutW = layoutViewportWidth();
  root.style.setProperty("--app-vvw", `${Math.round(layoutW)}px`);

  const trackBrowserChrome = shouldTrackBrowserChrome();
  root.classList.toggle("browser-chrome", trackBrowserChrome);

  if (isMobileShellViewport() || isMobileSheetViewport()) {
    const vv = window.visualViewport;
    const resolved = resolveMobileShellViewport({
      layoutHeight: layoutViewportHeight(),
      visualHeight: vv?.height ?? layoutViewportHeight(),
      visualOffsetTop: vv?.offsetTop ?? 0,
      visualWidth: vv?.width ?? layoutW,
      layoutWidth: layoutW,
      iosStandalone: isIosStandalonePwa(),
      safeBottom: readSeededSafeBottom(),
      nativeKeyboardHeight,
      trackBrowserChrome,
    });
    root.classList.toggle("keyboard-open", resolved.keyboardOpen);
    if (resolved.height === null) {
      // Keyboard closed on PWA / native / pinch-zoom: hand height back to CSS.
      root.style.removeProperty("--app-vvh");
      root.style.removeProperty("--app-vv-offset-top");
      root.style.removeProperty("--keyboard-inset-bottom");
    } else {
      root.style.setProperty("--app-vvh", `${resolved.height}px`);
      root.style.setProperty("--app-vv-offset-top", `${resolved.offsetTop}px`);
      if (nativeKeyboardHeight > 0) {
        root.style.setProperty(
          "--keyboard-inset-bottom",
          `${Math.round(nativeKeyboardHeight)}px`,
        );
      } else {
        root.style.removeProperty("--keyboard-inset-bottom");
      }
    }
  } else {
    root.classList.remove("keyboard-open");
    root.classList.remove("browser-chrome");
    root.style.removeProperty("--app-vvh");
    root.style.removeProperty("--app-vv-offset-top");
    root.style.removeProperty("--keyboard-inset-bottom");
  }
}

export function subscribeViewportMetrics(onChange: () => void) {
  const vv = window.visualViewport;
  requestOverlayVirtualKeyboard();

  const handler = () => {
    syncViewportMetrics();
    onChange();
  };

  let unbindNativeKeyboard = () => {};
  void bindNativeKeyboard(handler).then((unbind) => {
    unbindNativeKeyboard = unbind;
  });

  handler();
  window.addEventListener("resize", handler);
  window.addEventListener("orientationchange", handler);
  vv?.addEventListener("resize", handler);
  vv?.addEventListener("scroll", handler);

  return () => {
    unbindNativeKeyboard();
    window.removeEventListener("resize", handler);
    window.removeEventListener("orientationchange", handler);
    vv?.removeEventListener("resize", handler);
    vv?.removeEventListener("scroll", handler);
    document.documentElement.classList.remove("keyboard-open");
    document.documentElement.classList.remove("browser-chrome");
    document.documentElement.style.removeProperty("--app-vvw");
    document.documentElement.style.removeProperty("--app-vvh");
    document.documentElement.style.removeProperty("--app-vv-offset-top");
    document.documentElement.style.removeProperty("--keyboard-inset-bottom");
  };
}
